import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  ArrowLeft, AlertTriangle, ShieldAlert, Info, CheckCircle, RefreshCw,
  Camera, Trash2, Plus, X, ClipboardCheck, XCircle, MapPin, Lock,
  CheckCircle2, Upload, FileCheck
} from "lucide-react";

const SEVERITY_CONFIG = {
  critical: { label: "Critical", color: "bg-red-50 text-red-700", border: "border-red-200", icon: AlertTriangle },
  high:     { label: "High",     color: "bg-orange-50 text-orange-700", border: "border-orange-200", icon: ShieldAlert },
  medium:   { label: "Medium",   color: "bg-amber-50 text-amber-700", border: "border-amber-200", icon: Info },
  low:      { label: "Low",      color: "bg-green-50 text-green-700", border: "border-green-200", icon: CheckCircle },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Finding verification card ── */
function VerifyCard({ finding, locked, onVerify }) {
  const [notes, setNotes] = useState(finding.field_notes || "");
  const [saving, setSaving] = useState(false);
  const sevKey = (finding.severity || finding.category || "medium").toLowerCase();
  const sev = SEVERITY_CONFIG[sevKey] || SEVERITY_CONFIG.medium;
  const Icon = sev.icon;
  const status = finding.field_status;

  const act = async (field_status) => {
    setSaving(true);
    await onVerify(finding.id, field_status, notes);
    setSaving(false);
  };

  return (
    <div data-testid={`verify-card-${finding.id}`}
      className={`border rounded-xl p-4 transition-all ${
        status === "verified" ? "border-green-300 bg-green-50/40"
        : status === "not_found" ? "border-slate-300 bg-slate-50 opacity-75"
        : sev.border + " bg-white"
      }`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${sev.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-slate-800">{finding.title}</p>
            {finding.added_on_site && (
              <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-navy/10 text-navy">On-site</span>
            )}
            <span className={`text-xs font-medium ${sev.color.split(" ")[1]}`}>{sev.label}</span>
          </div>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{finding.description}</p>

          {status && (
            <div className={`mt-2 inline-flex items-center gap-1.5 text-xs font-semibold ${
              status === "verified" ? "text-green-700" : "text-slate-500"}`}
              data-testid={`field-status-${finding.id}`}>
              {status === "verified" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              {status === "verified" ? "Verified on-site" : "Not found on-site"}
              {finding.field_verified_by && <span className="text-slate-400 font-normal">· {finding.field_verified_by}</span>}
            </div>
          )}

          {!locked && (
            <>
              <textarea
                data-testid={`field-notes-${finding.id}`}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Field notes (optional) — conditions observed, corrective progress…"
                rows={2}
                className="mt-3 w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy resize-none"
              />
              <div className="mt-2 flex items-center gap-2">
                <button
                  data-testid={`verify-btn-${finding.id}`}
                  disabled={saving}
                  onClick={() => act("verified")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    status === "verified"
                      ? "bg-green-600 text-white"
                      : "bg-green-50 text-green-700 hover:bg-green-600 hover:text-white"
                  }`}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Verified
                </button>
                <button
                  data-testid={`not-found-btn-${finding.id}`}
                  disabled={saving}
                  onClick={() => act("not_found")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    status === "not_found"
                      ? "bg-slate-600 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-600 hover:text-white"
                  }`}>
                  <XCircle className="w-3.5 h-3.5" /> Not Found
                </button>
                {status && (
                  <button
                    data-testid={`reset-status-btn-${finding.id}`}
                    disabled={saving}
                    onClick={() => act("pending")}
                    className="text-xs text-slate-400 hover:text-slate-600 underline ml-1">
                    Reset
                  </button>
                )}
              </div>
            </>
          )}
          {locked && finding.field_notes && (
            <p className="mt-2 text-xs text-slate-500 italic">&ldquo;{finding.field_notes}&rdquo;</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Add on-site finding form ── */
function AddFindingForm({ inspectionId, inspectionType, onAdded, onClose }) {
  const [form, setForm] = useState({ title: "", category: "Medium", description: "", recommendation: "", library_ref: "" });
  const [saving, setSaving] = useState(false);
  const [library, setLibrary] = useState([]);

  useEffect(() => {
    api.get("/carrier/rec-library")
      .then(r => setLibrary(r.data.filter(e => e.inspection_type === "all" || e.inspection_type === inspectionType)))
      .catch(() => {});
  }, [inspectionType]);

  const applyLibrary = (id) => {
    const entry = library.find(e => e.id === id);
    if (!entry) { setForm(f => ({ ...f, library_ref: "" })); return; }
    setForm(f => ({
      ...f,
      title: f.title.trim() ? f.title : entry.title,
      recommendation: entry.text,
      library_ref: entry.title,
    }));
    toast.success(`Applied carrier standard: ${entry.title}`);
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const res = await api.post(`/inspections/${inspectionId}/field-findings`, form);
      onAdded(res.data);
      toast.success("On-site finding added");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add finding");
    }
    setSaving(false);
  };

  return (
    <div className="border border-navy/20 bg-navy/[0.03] rounded-xl p-4 space-y-3" data-testid="add-finding-form">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-navy">New On-Site Finding</p>
        <button onClick={onClose} data-testid="close-add-finding-btn" className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>
      {library.length > 0 && (
        <div>
          <select
            data-testid="library-pick-select"
            defaultValue=""
            onChange={e => applyLibrary(e.target.value)}
            className="w-full text-sm border border-teal-200 bg-teal-50/50 text-slate-700 rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400">
            <option value="">Pick from carrier standard recommendations… (optional)</option>
            {library.map(e => <option key={e.id} value={e.id}>{e.title}{e.category ? ` — ${e.category}` : ""}</option>)}
          </select>
          {form.library_ref && (
            <p className="text-xs text-teal-600 mt-1 font-medium" data-testid="library-applied-hint">
              Using carrier standard "{form.library_ref}" — edit the wording below for context as needed.
            </p>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          data-testid="new-finding-title-input"
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="Finding title *"
          className="sm:col-span-2 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy"
        />
        <select
          data-testid="new-finding-severity-select"
          value={form.category}
          onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy bg-white">
          {["Critical", "High", "Medium", "Low"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <textarea
        data-testid="new-finding-description-input"
        value={form.description}
        onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        placeholder="What did you observe?"
        rows={2}
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy resize-none"
      />
      <textarea
        data-testid="new-finding-recommendation-input"
        value={form.recommendation}
        onChange={e => setForm(f => ({ ...f, recommendation: e.target.value }))}
        placeholder="Recommended corrective action (optional)"
        rows={2}
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy resize-none"
      />
      <button
        data-testid="submit-new-finding-btn"
        disabled={saving}
        onClick={submit}
        className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 transition-colors disabled:opacity-50">
        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Finding
      </button>
    </div>
  );
}

/* ─── Main Page ── */
export default function FieldVisitView() {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [inspection, setInspection] = useState(null);
  const [findings, setFindings] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [summary, setSummary] = useState("");
  const [finalizing, setFinalizing] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inspRes, findRes, photoRes] = await Promise.all([
        api.get(`/inspections/${inspectionId}`),
        api.get(`/inspections/${inspectionId}/findings`).catch(() => ({ data: [] })),
        api.get(`/inspections/${inspectionId}/field-photos`).catch(() => ({ data: [] })),
      ]);
      setInspection(inspRes.data);
      setFindings(findRes.data.filter(f => !f.dismissed));
      setPhotos(photoRes.data);
      setSummary(inspRes.data.field_summary || "");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load inspection.");
    }
    setLoading(false);
  }, [inspectionId]);

  useEffect(() => { load(); }, [load]);

  const locked = inspection?.dispatch_status === "visit_completed";

  const handleVerify = async (findingId, field_status, field_notes) => {
    try {
      const res = await api.put(`/inspections/${inspectionId}/findings/${findingId}/field-verify`, { field_status, field_notes });
      setFindings(fs => fs.map(f => (f.id === findingId ? res.data : f)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update finding");
    }
  };

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      try {
        const res = await api.post(`/inspections/${inspectionId}/field-photos`, fd);
        setPhotos(p => [...p, res.data]);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    setCaption("");
    setUploading(false);
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await api.delete(`/inspections/${inspectionId}/field-photos/${photoId}`);
      setPhotos(p => p.filter(x => x.id !== photoId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete photo");
    }
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      const res = await api.post(`/inspections/${inspectionId}/complete-visit`, { field_summary: summary });
      setDone(res.data);
      toast.success("Visit completed — report finalized");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to finalize visit");
    }
    setFinalizing(false);
  };

  const dispositioned = findings.filter(f => f.field_status === "verified" || f.field_status === "not_found").length;
  const allDone = findings.length > 0 && dispositioned === findings.length;
  const progressPct = findings.length ? Math.round((dispositioned / findings.length) * 100) : 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-8 h-8 text-navy animate-spin" />
        </div>
      </Layout>
    );
  }

  if (error || !inspection) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <AlertTriangle className="w-10 h-10 text-red-400" />
          <p className="text-slate-700 font-medium">{error || "Inspection not found"}</p>
          <button onClick={() => navigate("/lc-dashboard")} className="text-navy text-sm underline">Back to Dashboard</button>
        </div>
      </Layout>
    );
  }

  /* ── Success state ── */
  if (done) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto mt-16 bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm animate-fade-in"
          data-testid="visit-complete-success">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileCheck className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-800">Visit Completed</h1>
          <p className="text-slate-500 text-sm mt-2">The report for <strong>{inspection.insured_name}</strong> has been finalized.</p>
          <div className="grid grid-cols-3 gap-3 mt-6">
            {[
              { label: "Verified", value: done.verified, color: "text-green-600" },
              { label: "Not Found", value: done.not_found, color: "text-slate-500" },
              { label: "Photos", value: done.photos, color: "text-navy" },
            ].map(s => (
              <div key={s.label} className="p-3 bg-slate-50 rounded-xl">
                <p className={`font-heading text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-3 mt-8">
            <button
              data-testid="back-to-queue-btn"
              onClick={() => navigate("/lc-dashboard")}
              className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200 transition-colors">
              Back to Work Queue
            </button>
            <button
              data-testid="view-final-report-btn"
              onClick={() => navigate(`/dashboard/report/${inspection.id}`)}
              className="px-4 py-2.5 bg-navy text-white rounded-xl text-sm font-medium hover:bg-navy/90 transition-colors">
              View Final Report
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="field-visit-view">
        <button
          data-testid="back-to-briefing-btn"
          onClick={() => navigate(`/lc/briefing/${inspectionId}`)}
          className="flex items-center gap-2 text-slate-500 hover:text-navy text-sm font-medium transition-colors group">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Pre-Briefing
        </button>

        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="bg-navy px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-1 text-white/60">On-Site Completion</p>
              <h1 className="font-heading text-2xl font-bold text-white">{inspection.insured_name}</h1>
              <p className="text-white/70 text-sm mt-1 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                {[inspection.property_address, inspection.city, inspection.state].filter(Boolean).join(", ")}
              </p>
            </div>
            {locked && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-green-500/20 text-green-200"
                data-testid="visit-locked-badge">
                <Lock className="w-3.5 h-3.5" /> Visit Completed
              </span>
            )}
          </div>
          {/* Progress bar */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Verification Progress</p>
              <p className="text-xs font-bold text-navy" data-testid="progress-count">{dispositioned} of {findings.length} findings</p>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${allDone ? "bg-green-500" : "bg-navy"}`}
                style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Findings checklist (2/3) */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-slate-800">Field Verification Checklist</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Confirm each AI finding on-site, or mark it not found</p>
                </div>
                {!locked && (
                  <button
                    data-testid="open-add-finding-btn"
                    onClick={() => setShowAdd(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-navy/10 text-navy hover:bg-navy hover:text-white rounded-lg text-xs font-semibold transition-colors">
                    <Plus className="w-3.5 h-3.5" /> Add Finding
                  </button>
                )}
              </div>
              <div className="p-5 space-y-3">
                {showAdd && !locked && (
                  <AddFindingForm
                    inspectionId={inspectionId}
                    inspectionType={inspection?.inspection_type}
                    onAdded={f => setFindings(fs => [...fs, f])}
                    onClose={() => setShowAdd(false)}
                  />
                )}
                {findings.length === 0 && !showAdd ? (
                  <div className="py-10 text-center">
                    <ClipboardCheck className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">No findings to verify — add on-site findings if hazards are observed</p>
                  </div>
                ) : (
                  findings.map(f => (
                    <VerifyCard key={f.id} finding={f} locked={locked} onVerify={handleVerify} />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right column: photos + finalize */}
          <div className="space-y-5">
            {/* Field photos */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="field-photos-card">
              <div className="flex items-center gap-2 mb-4">
                <Camera className="w-4 h-4 text-slate-500" />
                <h2 className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Field Photos</h2>
                <span className="ml-auto text-xs font-bold text-slate-400">{photos.length}</span>
              </div>

              {!locked && (
                <>
                  <input
                    data-testid="photo-caption-input"
                    value={caption}
                    onChange={e => setCaption(e.target.value)}
                    placeholder="Caption for next photo (optional)"
                    className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 mb-2 focus:outline-none focus:border-navy"
                  />
                  <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                    data-testid="field-photo-input"
                    onChange={e => { handleUpload(e.target.files); e.target.value = ""; }} />
                  <button
                    data-testid="upload-photo-btn"
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-slate-200 hover:border-navy text-slate-500 hover:text-navy rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
                    {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? "Uploading…" : "Add Photos"}
                  </button>
                </>
              )}

              {photos.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {photos.map(p => (
                    <div key={p.id} className="relative group rounded-lg overflow-hidden border border-slate-100" data-testid={`field-photo-${p.id}`}>
                      <img src={`${BACKEND_URL}${p.url}`} alt={p.caption || "Field photo"} className="w-full h-24 object-cover" />
                      {p.caption && (
                        <p className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 truncate">{p.caption}</p>
                      )}
                      {!locked && (
                        <button
                          data-testid={`delete-photo-btn-${p.id}`}
                          onClick={() => handleDeletePhoto(p.id)}
                          className="absolute top-1 right-1 w-6 h-6 bg-black/50 hover:bg-red-600 text-white rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Finalize */}
            <div className={`border rounded-2xl p-5 shadow-sm ${locked ? "bg-green-50/50 border-green-200" : "bg-white border-slate-200"}`}
              data-testid="finalize-card">
              <div className="flex items-center gap-2 mb-3">
                <FileCheck className="w-4 h-4 text-slate-500" />
                <h2 className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Finalize Report</h2>
              </div>
              {locked ? (
                <div className="text-sm text-green-700">
                  <p className="font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Visit completed</p>
                  {inspection.visit_completed_by_name && (
                    <p className="text-xs text-slate-500 mt-1">
                      By {inspection.visit_completed_by_name}
                      {inspection.visit_completed_at && ` on ${new Date(inspection.visit_completed_at).toLocaleDateString()}`}
                    </p>
                  )}
                  {inspection.field_summary && (
                    <p className="text-xs text-slate-600 italic mt-2">&ldquo;{inspection.field_summary}&rdquo;</p>
                  )}
                </div>
              ) : (
                <>
                  <textarea
                    data-testid="field-summary-input"
                    value={summary}
                    onChange={e => setSummary(e.target.value)}
                    placeholder="Visit summary — overall site condition, insured cooperation, follow-up needed…"
                    rows={4}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy resize-none"
                  />
                  {!allDone && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1.5" data-testid="finalize-blocked-msg">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      Verify all {findings.length} findings before finalizing ({findings.length - dispositioned} remaining)
                    </p>
                  )}
                  <button
                    data-testid="finalize-visit-btn"
                    disabled={!allDone || finalizing}
                    onClick={handleFinalize}
                    className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    {finalizing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
                    Complete Visit & Finalize
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
